import { Tabs, TabsList, TabsTrigger, TabsContent } from '@radix-ui/react-tabs';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HTTP } from '../api/http-common';
import Swal from "sweetalert2";
import { SearchIcon } from "lucide-react";

const MemberEdit = () => {
    const { id } = useParams();
    const memberId = Number(id);

    const navigate = useNavigate();
    const access_token = (localStorage.getItem('access_token'));

    const [selectedTab, setSelectedTab] = useState("profile", "transaction-history");
    const [isEditable, setIsEditable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [filterType, setFilterType] = useState("all");

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        plan_amount: '',
        plan_type: '',
        plan_start_date: '',
        plan_expiration_date: '',
        transaction_history: [],
    });


    const fetchMembersDetails = async (planType = "all", searchTerm = "") => {
        let config = {
            headers: { 'Content-Type': 'application/json', 'token': access_token },
        };

        // Construct query params
        let queryParams = new URLSearchParams();
        if (planType && planType !== "all") { // Only add if filtering
            queryParams.append("plan_type", planType);
        }
        if (searchTerm) {
            queryParams.append("search", searchTerm); // Add search parameter
        }
        try {
            const response = await HTTP.get(`user/${memberId}?${queryParams.toString()}`, config);

            if (response.status === 200) {
                const data = response.data;

                if (data) {
                    // Get the latest transaction
                    const latestTransaction = data.transaction_history?.length > 0
                        ? data.transaction_history[data.transaction_history.length - 1]
                        : {};

                    setFormData({
                        first_name: data.first_name || '',
                        last_name: data.last_name || '',
                        email: data.email || '',
                        plan_amount: data.plan_amount || '',
                        plan_type: data.plan_type || '',
                        plan_start_date: data.plan_start_date || '',
                        plan_expiration_date: data.plan_expiration_date || '',
                        transaction_history: data.transaction_history || [],
                    });
                }
            }
        } catch (error) {
            console.log("Error fetching member details:", error);
        }
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        const newFilter = e.target.value;
        setFilterType(newFilter);
        fetchMembersDetails(newFilter); // Fetch with new filter
    };

    // Handle search enter
    const onSearchEnter = (value) => {
        setSearchValue(value);
        fetchMembersDetails(filterType, value); // Keep current filter while searching
    };


    useEffect(() => {
        fetchMembersDetails();
    }, []);

    const handleEditToggle = () => {
        setIsEditable(prevState => !prevState);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const userId = memberId;
        setIsLoading(true);
        setError(null);

        try {
            const response = await HTTP.put(`/user/update/${userId}`, formData, {
                headers: {
                    "Content-Type": "application/json",
                    token: access_token,
                },
            });

            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: "Member Updated Succesfully",
                });
                navigate('/members');
            }
        } catch (error) {
            console.error("Error updating user:", error);
            setError("Failed to update user. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="container mx-auto p-8">
                <div className="bg-white rounded-lg p-6  border-gray-200 border min-h-40 flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-600 text-xl font-semibold">
                                    {formData.first_name.charAt(0)}
                                </span>
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">{formData.first_name}</h3>
                                <p className="text-gray-600 text-sm">{formData.email}</p>
                            </div>
                        </div>
                    </div>

                    <Tabs defaultValue={selectedTab} className="TabsRoot">
                        <TabsList className="TabsList justify-start gap-6">
                            <TabsTrigger
                                value="profile"
                                className={`text-left justify-start w-0 p-0 TabsTrigger h-[60px] font-medium ${selectedTab === "profile" ? "data-[state=active]" : ""
                                    }`}
                                onClick={() => setSelectedTab?.("profile")}
                            >
                                Personal Information
                            </TabsTrigger>
                            <TabsTrigger
                                value="transaction-history"
                                className={`text-left justify-start w-0 p-0 TabsTrigger h-[60px] font-medium ${selectedTab === "transaction-history" ? "data-[state=active]" : ""
                                    }`}
                            //   onClick={() => setSelectedTab?.("transaction-history")}
                            >
                                Plan History
                            </TabsTrigger>
                        </TabsList>
                        {formData.plan_amount && (
                            <div className="active-plan-container">
                                <p>
                                    <strong>
                                        Last Purchased Plan - {formData.plan_type === "month" ? (
                                            <>
                                                Monthly: ${formData.plan_amount} - Expiry Date: {formData.plan_expiration_date}
                                            </>
                                        ) : (
                                            <>
                                                Annual: ${formData.plan_amount} - Expiry Date: {formData.plan_expiration_date}
                                            </>
                                        )}
                                    </strong>
                                </p>
                            </div>
                        )}
                        <form className="space-y-4 mt-8"
                            onSubmit={handleSubmit}
                        >
                            <TabsContent value="profile">
                                <div className="flex justify-end">
                                    <button
                                        type='button'
                                        onClick={handleEditToggle}
                                        className="theme-button text-white px-4 py-2 rounded-md border-gray-200 border"
                                    >
                                        {isEditable ? "Disable Edit" : "Enable Edit"}
                                    </button>
                                </div>
                                <div className="bg-white rounded-lg p-6 border-gray-200 border">
                                    <h3 className="font-medium text-lg mb-4">Info</h3>
                                    <div className="grid grid-cols-2 gap-4">

                                        <div className="space-y-2">
                                            <label htmlFor="first_name" className="text-sm font-medium text-gray-500">
                                                First Name
                                            </label>
                                            <input
                                                type="text"
                                                id="first_name"
                                                name="first_name"
                                                className={`peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 peer h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400 ${!isEditable ? 'text-gray-500 bg-gray-100 cursor-not-allowed' : ''}`}
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                disabled={!isEditable}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="last_name" className="text-sm font-medium text-gray-500">
                                                Last Name
                                            </label>
                                            <input
                                                type="text"
                                                id="last_name"
                                                name="last_name"
                                                className={`peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 peer h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400 ${!isEditable ? 'text-gray-500 bg-gray-100 cursor-not-allowed' : ''}`}
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                disabled={!isEditable}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="email" className="text-sm font-medium text-gray-500">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                className={`peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 peer h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400 ${!isEditable ? 'text-gray-500 bg-gray-100 cursor-not-allowed' : ''}`}
                                                value={formData.email}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </div>

                                        {/* <div className="space-y-2">
                                            <label htmlFor="plan_type" className="text-sm font-medium text-gray-500">
                                                Plan Type
                                            </label>
                                            <input
                                                type="text"
                                                id="plan_type"
                                                name="plan_type"
                                                className={`peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 peer h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400 ${!isEditable ? 'text-gray-500 bg-gray-100 cursor-not-allowed' : ''}`}
                                                value={formData.plan_type}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="plan_amount" className="text-sm font-medium text-gray-500">
                                                Plan Amount
                                            </label>
                                            <input
                                                type="text"
                                                id="plan_amount"
                                                name="plan_amount"
                                                className={`peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 peer h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400 ${!isEditable ? 'text-gray-500 bg-gray-100 cursor-not-allowed' : ''}`}
                                                value={formData.plan_amount}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </div> */}
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="theme-button text-white px-4 py-2 rounded-md border-gray-200 border"
                                    disabled={!isEditable}
                                    style={{
                                        backgroundColor: isEditable ? "#6c1cff" : "#ccc",
                                        cursor: isEditable ? "pointer" : "not-allowed",
                                        opacity: isEditable ? 1 : 0.6,
                                        marginTop: "20px",
                                    }}
                                >
                                    Submit
                                </button>
                            </TabsContent>

                            <TabsContent value="transaction-history">
                                <div className="bg-white">
                                    <h3 className="font-bold text-xl mb-5">Plan History</h3>
                                    <div className="flex justify-between items-center">
                                        <div className="w-1/2 flex">
                                            <div className="w-1/2 border-b border-gray-300 mr-6">
                                                <div className="flex items-center">
                                                    <span className="w-4">
                                                        <SearchIcon className="w-4" />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        placeholder="Search Plan"
                                                        className="admin-table-search-input w-full"
                                                        value={searchValue}
                                                        onChange={(e) => setSearchValue(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') { onSearchEnter(e.target.value) } }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-1/2 border-b border-gray-300">
                                                <select
                                                    className="admin-table-filter-dropdown w-full"
                                                    value={filterType}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="all">All Types</option>
                                                    <option value="month">Monthly</option>
                                                    <option value="annual">Annually</option>
                                                </select>
                                            </div>
                                        </div>

                                    </div>
                                    {Array.isArray(formData.transaction_history) && formData.transaction_history.length > 0 ? (
                                        <table className="admin-table-table mt-5">
                                            <thead>
                                                <tr>
                                                    <th className="admin-table-th">Plan Amount</th>
                                                    <th className="admin-table-th">Plan Type</th>
                                                    <th className="admin-table-th">Checkout Session Id</th>
                                                    <th className="admin-table-th">Transaction ID</th>
                                                    <th className="admin-table-th">Type</th>
                                                    <th className="admin-table-th">Message</th>
                                                    <th className="admin-table-th">Status</th>
                                                    <th className="admin-table-th">Created At</th>
                                                    <th className="admin-table-th">Updated At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.transaction_history.map((transaction) => (
                                                    <tr key={transaction.id}>
                                                        <td className="admin-table-th-td">{transaction.plan_amount}</td>
                                                        <td className="admin-table-th-td">{transaction.plan_type}</td>
                                                        <td className="admin-table-th-td">{transaction.checkoutSessionId}</td>
                                                        <td className="admin-table-th-td">{transaction.transaction_id}</td>
                                                        <td className="admin-table-th-td">{transaction.type}</td>
                                                        <td className="admin-table-th-td">{transaction.message}</td>
                                                        <td className="admin-table-th-td">{transaction.status === "1" ? "Active" : "Inactive"}</td>
                                                        <td className="admin-table-th-td">{new Date(transaction.created_at).toLocaleDateString()}</td>
                                                        <td className="admin-table-th-td">{new Date(transaction.updated_at).toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="text-gray-500 mt-4">No plan history available.</p>
                                    )}

                                </div>
                            </TabsContent>
                        </form>
                    </Tabs>

                </div>
            </div>
        </>
        // <div>This is Member Edit</div>
    )
}

export default MemberEdit